import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useState, useCallback, useContext } from "react";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import API_URLS from "../../../constants/apiConstants";
import { useNavigate } from "react-router-dom";
import { BiX } from "react-icons/bi";
import "../css/RoomModal.css";
import AuthContext from "../../../context/AuthProvider";
import { useTranslation } from "react-i18next";
import { ADMIN_STUDENTS_ROUTE, ADMIN_STAFF_ROUTE } from "../../../routes/RoutesPathes";

export default function AddModal({ open, onClose, title, classId }) {
  const axiosPrivate = useAxiosPrivate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { school } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const resetForm = useCallback(() => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setErrors({});
  }, []);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateForm = () => {
    const newErrors = {};
    const type = title?.toLowerCase();
    if (!firstName.trim()) {
      newErrors.firstName = isAr ? "الاسم الأول مطلوب" : "First name is required";
    }
    if (!lastName.trim()) {
      newErrors.lastName = isAr ? "الاسم الأخير مطلوب" : "Last name is required";
    }


    if (type === 'staff') {
      if (!email.trim()) {
        newErrors.email = isAr ? "البريد الإلكتروني مطلوب" : "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(email)) {
        newErrors.email = isAr ? "صيغة البريد غير صحيحة" : "Invalid email format";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const type = title.toLowerCase();

      const formData = {
        FirstName: firstName,
        LastName: lastName, 
      };

      if(type === 'student'){
        formData.Nursery = school.id;
        if (classId) {
          formData.Classes = [classId];
        }
      }else if (type === 'staff') {
        formData.Nursery_id = school.id;
        formData.email = email;
      }
      else{
        formData.Nursery_id = school.id;
      }
      const url = type === 'student'?API_URLS.student:API_URLS.staff;
      const response = await axiosPrivate.post(url, formData, {
        headers: { "Content-Type": "application/json" }
      });

      handleClose();
      
      const id = response.data[type]?.id;
      if (type === 'student') {
      navigate(`${ADMIN_STUDENTS_ROUTE}/details/${id}`);
    } else if (type === 'staff') {
      navigate(`${ADMIN_STAFF_ROUTE}/details/${id}`);
    }
    } catch (error) {
      console.error("Request error:", error);
      setErrors((prev) => ({
        ...prev,
        server:
          error.response?.data?.message ||
          t("form.genericError")
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className="modalStyle">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Typography variant="h5" fontWeight="bold" color="secondary">
            {t("modal.titleInfo", { title: t(`admin_titles.${title}`) })}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <BiX />
          </IconButton>
        </Box>

{/* حقول الاسم (FirstName & LastName) بجانب بعضهما */}
        <Box display="flex" gap={2} mb={3}>
          <Box flex={1}>
            <Typography variant="body1" component="label" color="secondary" gutterBottom>
              {isAr ? "الاسم الأول" : "First Name"} <span className="text-danger">*</span>
            </Typography>
            <input
              type="text"
              value={firstName}
              className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
              onChange={(e) => {
                setFirstName(e.target.value);
                setErrors((prev) => ({ ...prev, firstName: undefined }));
              }}
              placeholder={isAr ? "الاسم الأول" : "First Name"}
              disabled={isLoading}
            />
            {errors.firstName && <Typography className="errorText">{errors.firstName}</Typography>}
          </Box>

          <Box flex={1}>
            <Typography variant="body1" component="label" color="secondary" gutterBottom>
              {isAr ? "الاسم الأخير" : "Last Name"} <span className="text-danger">*</span>
            </Typography>
            <input
              type="text"
              value={lastName}
              className={`form-control ${errors.lastName ? "is-invalid" : ""}`}
              onChange={(e) => {
                setLastName(e.target.value);
                setErrors((prev) => ({ ...prev, lastName: undefined }));
              }}
              placeholder={isAr ? "الاسم الأخير" : "Last Name"}
              disabled={isLoading}
            />
            {errors.lastName && <Typography className="errorText">{errors.lastName}</Typography>}
          </Box>
        </Box>

        {title?.toLowerCase() === "staff" && (
          <Box mb={3}>
            <Typography
              variant="body1"
              component="label"
              display="block"
              color="secondary"
              gutterBottom
            >
              {isAr ? "البريد الإلكتروني" : "Email Address"} <span className="text-danger">*</span>
            </Typography>
            <input
              type="email"
              value={email}
              className={`form-control ${errors.email ? "is-invalid" : ""}`}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors((prev) => ({ ...prev, email: undefined }));
              }}
              placeholder="example@mail.com"
              disabled={isLoading}
            />
            {errors.email && (
              <Typography className="errorText">{errors.email}</Typography>
            )}
          </Box>
        )}


        {errors.server && (
          <Typography className="errorText" mb={2}>
            {errors.server}
          </Typography>
        )}

        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading}
            startIcon={
              isLoading ? <CircularProgress size={20} color="inherit" /> : null
            }
            className="submitButton"
          >
           {t("modal.addTitle", { title: t(`admin_titles.${title}`) })}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
